<template>
  <div class="equity-changes">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="grid-template-columns: repeat(2, minmax(160px, 220px))">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-flag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Opening Equity</div>
          <div class="rp-card-value">{{ formatMoney(summary.opening) }}</div>
          <div class="rp-card-sub">on {{ formatDate(filters.date_from) }}</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-cash-banknote"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Capital Added</div>
          <div class="rp-card-value">{{ formatMoney(summary.capital_added) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.withdrawn_capital) }} withdrawn</div>
        </div>
      </div>
      <div class="rp-card" :class="Number(summary.net_profit) < 0 ? 'tone-red' : 'tone-green'">
        <span class="rp-card-icon"><i class="ti" :class="Number(summary.net_profit) < 0 ? 'ti-trending-down' : 'ti-trending-up'"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">{{ Number(summary.net_profit) < 0 ? 'Net Loss' : 'Net Profit' }}</div>
          <div class="rp-card-value" :class="Number(summary.net_profit) < 0 ? 'text-danger' : 'text-success'">
            {{ formatMoney(summary.net_profit) }}
          </div>
          <div class="rp-card-sub">of the period</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-user-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Equity</div>
          <div class="rp-card-value">{{ formatMoney(summary.closing) }}</div>
          <div class="rp-card-sub">
            <span :class="netClass(summary.change)">{{ signedMoney(summary.change) }}</span>
            <template v-if="summary.growth !== null"> ({{ summary.growth }}%)</template>
          </div>
        </div>
      </div>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }" style="grid-template-columns: 1fr">
      <div class="rp-panel">
        <div class="rp-panel-title">How equity moved</div>
        <div class="row g-3">
          <div v-for="step in flowBars" :key="step.label" class="col">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ step.label }}</span>
              <span class="fw-semibold text-nowrap" :class="Number(step.value) ? step.text : 'text-muted'">{{ Number(step.value) ? step.sign : '' }}{{ formatMoney(step.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="step.tone" :style="{ width: `${step.percent}%` }"></div></div>
          </div>
        </div>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold">Statement of changes in equity <span class="text-muted fw-normal small">· {{ periodText() }}</span></div>
      </div>
      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Equity</th>
              <th class="text-end">Opening</th>
              <th class="text-end">Added</th>
              <th class="text-end">Withdrawn / Loss</th>
              <th class="text-end">Closing</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <td>
                <button v-if="row.code" type="button" class="acc-link fw-medium" title="Open this account's ledger" @click="openLedger(row.code)">
                  {{ row.name }}
                </button>
                <span v-else class="fw-medium">{{ row.name }}</span>
                <span v-if="row.code" class="acc-code ms-1">{{ row.code }}</span>
                <span v-else class="rp-sub">income less expenses</span>
              </td>
              <td class="text-end" :class="{ 'text-danger': Number(row.opening) < 0 }">{{ formatMoney(row.opening) }}</td>
              <td class="text-end" :class="Number(row.added) ? 'text-success' : 'text-muted'">{{ amountText(row.added) }}</td>
              <td class="text-end" :class="Number(row.withdrawn) ? 'text-danger' : 'text-muted'">{{ amountText(row.withdrawn) }}</td>
              <td class="text-end fw-semibold" :class="{ 'text-danger': Number(row.closing) < 0 }">{{ formatMoney(row.closing) }}</td>
            </tr>
            <tr v-if="!loading && !rows.length">
              <td colspan="5">
                <div class="rp-empty">
                  <i class="ti ti-user-dollar"></i>
                  <div class="fw-semibold">No equity movements</div>
                  <small class="text-muted">Try another period.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th>Total equity</th>
              <th class="text-end">{{ formatMoney(summary.opening) }}</th>
              <th class="text-end">{{ formatMoney(summary.added) }}</th>
              <th class="text-end">{{ formatMoney(summary.withdrawn) }}</th>
              <th class="text-end">{{ formatMoney(summary.closing) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <p class="rp-hint mt-2 mb-0">
      Equity accounts are credit − debit: credits add capital, debits (e.g. owner's drawings) take it out. Retained
      profit is income less expenses before the period, plus the period's profit (or less its loss). Closing equity
      matches the equity on the balance sheet at the end date.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  opening: 0, added: 0, withdrawn: 0, closing: 0, net_profit: 0, retained_opening: 0, capital_added: 0,
  withdrawn_capital: 0, change: 0, growth: null,
};

export default {
  name: 'EquityChangesReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.equity_changes_report.get,
  fileName: 'changes_in_equity',
  columns: [],

  data() {
    return {
      filters: { ...defaultPeriod(), search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged;
    },

    visibleRows() {
      return this.rows;
    },

    flowBars() {
      const s = this.summary;
      const profit = Number(s.net_profit) || 0;
      const steps = [
        { label: 'Opening equity', value: s.opening, sign: '', tone: 'info', text: '' },
        { label: 'Capital added', value: s.capital_added, sign: '+', tone: 'success', text: 'text-success' },
        { label: profit < 0 ? 'Net loss' : 'Net profit', value: Math.abs(profit), sign: profit < 0 ? '−' : '+', tone: profit < 0 ? 'danger' : 'success', text: profit < 0 ? 'text-danger' : 'text-success' },
        { label: 'Capital withdrawn', value: s.withdrawn_capital, sign: '−', tone: 'danger', text: 'text-danger' },
        { label: 'Closing equity', value: s.closing, sign: '', tone: 'warning', text: '' },
      ];
      const max = Math.max(0, ...steps.map((st) => Math.abs(Number(st.value) || 0)));
      return steps.map((st) => {
        const v = Math.abs(Number(st.value) || 0);
        return { ...st, percent: max && v ? Math.max(4, Math.round((v / max) * 100)) : 0 };
      });
    },
  },

  methods: {
    extraParams() {
      return this.periodParams();
    },

    resetExtraFilters() {
      return defaultPeriod(this.today);
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    printReport() {
      const s = this.summary;
      this.printStatement(
        'Changes in Equity',
        [this.periodText(), this.storeLabel()],
        [['Opening equity', this.formatMoney(s.opening)], ['Capital added', this.formatMoney(s.capital_added)],
          [Number(s.net_profit) < 0 ? 'Net loss' : 'Net profit', this.formatMoney(s.net_profit)], ['Closing equity', this.formatMoney(s.closing)]],
        [
          { label: 'Code', value: (r) => r.code },
          { label: 'Equity', value: (r) => r.name },
          { label: 'Opening', value: (r) => this.formatMoney(r.opening), num: true },
          { label: 'Added', value: (r) => this.amountText(r.added), num: true },
          { label: 'Withdrawn / Loss', value: (r) => this.amountText(r.withdrawn), num: true },
          { label: 'Closing', value: (r) => this.formatMoney(r.closing), num: true },
        ],
        this.rows,
        { Equity: 'Total equity', Opening: this.formatMoney(s.opening), Added: this.formatMoney(s.added), 'Withdrawn / Loss': this.formatMoney(s.withdrawn), Closing: this.formatMoney(s.closing) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
